import React, { useCallback, useEffect, useState } from "react";
import Breadcrumbs from "../../../components/ui/Breadcrumbs.jsx";
import IndicesConfig from "./IndicesConfig.jsx";
import axios from "axios";
import { errorNotify, successNotify } from "../../../Services/Notification.js";
import RadioButton from "../../../components/ui/RadioButton.jsx";
import IndicesHeader from "../../../components/import/indices/IndicesHeaders.jsx";

const indicesAPIURL = `${process.env.REACT_APP_API_URL}api/import-index-manually`;

const getIndexDate = (row) =>
  row.date || row.Date || row.TradDt || row.trade_date || row.import_date;

const getIndexExchange = (row) => row.exchange || row.Exchange || "";

const exchangeOrder = { NSE: 0, BSE: 1 };

const sortIndicesData = (indicesData) => {
  if (!Array.isArray(indicesData)) return indicesData;

  return [...indicesData].sort((a, b) => {
    const dateA = new Date(getIndexDate(a)).getTime();
    const dateB = new Date(getIndexDate(b)).getTime();
    const safeDateA = Number.isNaN(dateA) ? Number.MAX_SAFE_INTEGER : dateA;
    const safeDateB = Number.isNaN(dateB) ? Number.MAX_SAFE_INTEGER : dateB;

    if (safeDateA !== safeDateB) return safeDateA - safeDateB;

    const exchangeA = getIndexExchange(a).toUpperCase();
    const exchangeB = getIndexExchange(b).toUpperCase();

    return (
      (exchangeOrder[exchangeA] ?? Number.MAX_SAFE_INTEGER) -
      (exchangeOrder[exchangeB] ?? Number.MAX_SAFE_INTEGER)
    );
  });
};

const Indices = () => {
  const [renderKey, setRenderKey] = useState(0);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState();
  const [loading, setLoading] = useState();
  const [exchange, setExchange] = useState("NSE");

  const triggerRerender = () => {
    setRenderKey((prev) => prev + 1);
  };

  const headers = {
    Authorization: `Bearer ${
      sessionStorage.getItem("subscriberToken")
        ? sessionStorage.getItem("subscriberToken")
        : sessionStorage.getItem("token")
    }`,
    "Content-Type": "application/json",
  };

  const getIndicesData = async () => {
    setLoading(true);
    try {
      if (filter?.bookclose_recorddate && filter?.nse_exdate) {
        const res = await axios.get(indicesAPIURL, {
          headers,
          params: {
            from_date: filter.bookclose_recorddate,
            to_date: filter.nse_exdate,
          },
        });

        if (!res.data?.error) {
          const responseData = sortIndicesData(res.data);
          setData(responseData);
        } else {
          errorNotify(res.data.message);
        }
      } else if (filter?.bookclose_recorddate && !filter?.nse_exdate) {
        errorNotify("Please select end date");
      } else if (!filter?.bookclose_recorddate && filter?.nse_exdate) {
        errorNotify("Please select start date");
      } else if (!filter?.bookclose_recorddate && !filter?.nse_exdate) {
        errorNotify("Please select start and end date");
      }
      setLoading(false);
    } catch (err) {
      errorNotify("Something went wrong");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filter?.bookclose_recorddate || filter?.nse_exdate) {
      getIndicesData();
    } else if (!filter) {
      setData([]);
      setLoading(false);
    }
  }, [filter?.bookclose_recorddate, filter?.nse_exdate, renderKey]);

  const handleReload = async (date) => {
    try {
      if (date) {
        const res = await axios.post(
          indicesAPIURL,
          { date, exchange },
          { headers },
        );

        if (!res.data?.error) {
          successNotify(res.data?.message || "Indices imported successfully");
          triggerRerender();
          return true;
        } else {
          errorNotify(res.data?.message || res.data?.error || "Import failed");
          return true;
        }
      }
    } catch (err) {
      errorNotify("Something went wrong");
      triggerRerender();
      return true;
    }
  };

  const handleExchangeChange = useCallback((value) => {
    setExchange(value);
  }, []);

  const buttonConfig = {
    handleReload,
  };

  const breadcrumbs = ["Home", "Import", "Indices"];

  return (
    <div className=" app-container min-h-[65vh] pb-[60px] pt-[100px]">
      <div className="flex justify-between">
        <Breadcrumbs items={breadcrumbs} />
        <div className="flex gap-4">
          <RadioButton
            label="NSE"
            value="NSE"
            checked={exchange === "NSE"}
            onChange={handleExchangeChange}
          />
          <RadioButton
            label="BSE"
            value="BSE"
            checked={exchange === "BSE"}
            onChange={handleExchangeChange}
          />
        </div>
      </div>

      <IndicesHeader setFilter={setFilter} />
      <IndicesConfig
        tableData={data}
        buttonConfig={buttonConfig}
        loading={loading}
        exchange={exchange}
      />
    </div>
  );
};

export default Indices;
